import { SerializedError } from "@reduxjs/toolkit";
import axios from "axios";
import applyCaseMiddleware from "axios-case-converter";

import { ArchiveMetadataService } from "@/api/services/interfaces";
import { routes } from "@/api/services/routes";
import { lvtResponseArchiveImagesNavMetaValidator } from "@/api/validation/archiveImagesNavMeta";
import { lvtResponseCameraOrCabinetMetadataValidator } from "@/api/validation/cameraOrCabinetMeta";
import { logError } from "@/utilities/api";
import {
    ArchiveImagesNavMeta,
    Cabinet,
    Camera,
    CameraOrCabinetMetadata,
    CameraOrCabinetMetadataResponse,
} from "@/utilities/types/archive";


axios.defaults.withCredentials = true;

const client = applyCaseMiddleware(axios.create());

export const archiveMetadataService: ArchiveMetadataService = {
    async fetchLiveUnitMetadata(liveUnitId: number): Promise<ArchiveImagesNavMeta | Error> {
        const route = routes.getLiveUnitMetaData(liveUnitId);

        try {
            const response = await client.get(route);

            if (lvtResponseArchiveImagesNavMetaValidator(response.data)) {
                return response.data.data as ArchiveImagesNavMeta; //TODO: conversion?
            } else {
                const error: SerializedError = {
                    name: "validation",
                    message: JSON.stringify(lvtResponseArchiveImagesNavMetaValidator.errors),
                };

                throw error;
            }
        } catch (error) {
            logError(route, error as Error);
            throw error;
        }
    },
    async fetchArchiveCabinetMetadata(archiveCabinetId: number): Promise<ArchiveImagesNavMeta | Error> {
        const route = routes.getArchiveCabinetMetaData(archiveCabinetId);

        try {
            const response = await client.get(route);

            if (lvtResponseArchiveImagesNavMetaValidator(response.data)) {
                return response.data.data as ArchiveImagesNavMeta; //TODO: conversion?
            } else {
                const error: SerializedError = {
                    name: "validation",
                    message: JSON.stringify(lvtResponseArchiveImagesNavMetaValidator.errors),
                };

                throw error;
            }
        } catch (error) {
            logError(route, error as Error);
            throw error;
        }
    },
    async fetchCameraOrCabinetMetadata(
        cameraIds: number[] | null,
        archiveCabinetIds: number[] | null,
    ): Promise<CameraOrCabinetMetadata[] | Error> {
        const route = routes.getCameraOrCabinetMetadata();

        try {
            const response = await client.post(route, { cameraIds, archiveCabinetIds });

            if (lvtResponseCameraOrCabinetMetadataValidator(response.data)) {
                const responseData: CameraOrCabinetMetadataResponse[] = response.data.data;

                const convertedData = responseData.map((data: CameraOrCabinetMetadataResponse) => {
                    const recordType = (): Camera | Cabinet => {
                        if (data.record.hasOwnProperty("camera")) return data.record.camera as Camera;
                        else return data.record.cabinet as Cabinet;
                    };

                    return {
                        record: recordType(),
                        archivedVideos: data.archivedVideos,
                        presets: data.presets,
                    };
                });

                return convertedData;
            } else {
                const error: SerializedError = {
                    name: "validation",
                    message: JSON.stringify(lvtResponseCameraOrCabinetMetadataValidator.errors),
                };

                throw error;
            }
        } catch (error) {
            logError(route, error as Error);
            throw error;
        }
    },
};

export const dummyArchiveMetadataService: ArchiveMetadataService = {
    async fetchLiveUnitMetadata(): Promise<ArchiveImagesNavMeta | Error> {
        const error = {
            name: "dummy",
            message: "Using the dummy service",
        };

        throw error;
    },
    async fetchArchiveCabinetMetadata(): Promise<ArchiveImagesNavMeta | Error> {
        const error = {
            name: "dummy",
            message: "Using the dummy service",
        };

        throw error;
    },
    async fetchCameraOrCabinetMetadata(): Promise<CameraOrCabinetMetadata[] | Error> {
        const error = {
            name: "dummy",
            message: "Using the dummy service",
        };

        throw error;
    },
};
